import React from 'react'
import {
  GoogleMap as GoogleMapAPI,
  Libraries,
  LoadScript,
} from '@react-google-maps/api'

import { AdvancedMapMarker } from './AdvancedMapMarker'
import { MapLocationProps, MarkerProps, StoreMapProps } from './MapTypes'
import { MapContainer } from './styles'
import { createMapLoadHandler, createMarkerClickHandler } from './utils'

const containerStyle = {
  width: '100%',
  height: '100%',
}

const googleMapsLibraries: Libraries = ['places', 'marker']

export const StoreMap = React.memo(
  ({
    apiKey,
    markers = [],
    center,
    selectedMarkerId,
    defaultPinColor = null,
    hoverPinColor = null,
    selectedPinColor = null,
    defaultZoom,
    selectedPinZoom,
    showMarkerInfoWindow = true,
    mapReadyCallback,
    pinClickCallback,
    mapControlOptions,
    storeDetailsHref,
    cardText,
    scriptLoaded,
    onMapChange,
    enableSearchOnMapLocationChange = false,
  }: StoreMapProps) => {
    const isPinClickInProgress = React.useRef(false)
    const [map, setMap] = React.useState<google.maps.Map | null>()
    const lastMarkerCountRef = React.useRef(0)
    const listenersRef = React.useRef<google.maps.MapsEventListener[]>([])

    // Fit bounds =>> View all the pins at one single frame
    const calculateBounds = React.useCallback(() => {
      if (!markers.length) return null

      const bounds = new window.google.maps.LatLngBounds()
      markers.forEach((marker: MarkerProps) => {
        bounds.extend({ lat: marker.position.lat, lng: marker.position.lng })
      })
      return bounds
    }, [markers])

    const fitBounds = React.useCallback(() => {
      if (!map) return

      const bounds = calculateBounds()
      if (!bounds) return

      map.fitBounds(bounds)
    }, [map, calculateBounds])

    React.useEffect(() => {
      if (
        enableSearchOnMapLocationChange &&
        map &&
        markers.length > 0 &&
        markers.length !== lastMarkerCountRef.current
      ) {
        lastMarkerCountRef.current = markers.length
        fitBounds()
      }
    }, [markers, map, enableSearchOnMapLocationChange, fitBounds])

    const onLoad = React.useCallback(
      (map: google.maps.Map) => {
        const handler = createMapLoadHandler({
          defaultZoom,
          mapReadyCallback,
          onMapChange,
          enableSearchOnMapLocationChange,
          isPinClickInProgress,
          listenersRef,
          setMap,
        })

        handler(map)
      },
      [
        defaultZoom,
        mapReadyCallback,
        onMapChange,
        enableSearchOnMapLocationChange,
        isPinClickInProgress,
        listenersRef,
        setMap,
      ],
    )

    const onUnmount = React.useCallback((_map: google.maps.Map) => {
      listenersRef.current.forEach((listener) => {
        google.maps.event.removeListener(listener)
      })
      listenersRef.current = []
      setMap(null)
    }, [])

    // Marker Click handler
    const handleMarkerClick = React.useCallback(
      (
        markerPosition: MapLocationProps,
        isInfoWindowOpen: boolean,
        markerId: number | null,
      ) => {
        const handler = createMarkerClickHandler({
          markers,
          fitBounds,
          defaultZoom,
          selectedPinZoom,
          pinClickCallback,
          isPinClickInProgress,
          map: map || null,
        })

        handler(markerPosition, isInfoWindowOpen, markerId)
      },
      [
        map,
        markers,
        selectedPinZoom,
        defaultZoom,
        isPinClickInProgress,
        pinClickCallback,
        fitBounds,
      ],
    )

    const markerComponents = React.useMemo(() => {
      return markers.map((marker, index) => (
        <AdvancedMapMarker
          key={`marker-${
            marker.store?.id || marker.markerId || `idx-${index}`
          }`}
          onMarkerClick={handleMarkerClick}
          {...marker}
          counter={index + 1}
          mapInstance={map}
          selectedMarkerId={selectedMarkerId}
          defaultPinColor={defaultPinColor}
          hoverPinColor={hoverPinColor}
          selectedPinColor={selectedPinColor}
          showMarkerInfoWindow={showMarkerInfoWindow}
          storeDetailsHref={storeDetailsHref}
          cardText={cardText}
        />
      ))
    }, [
      markers,
      handleMarkerClick,
      map,
      selectedMarkerId,
      defaultPinColor,
      hoverPinColor,
      selectedPinColor,
      showMarkerInfoWindow,
      storeDetailsHref,
      cardText,
    ])
    const mapId = 'store-locator-map'

    const mapOptions = React.useMemo(() => {
      return mapControlOptions
        ? { options: { ...mapControlOptions, mapId } }
        : {}
    }, [mapControlOptions])

    return (
      <MapContainer>
        {!scriptLoaded ? (
          <LoadScript
            id="google-map"
            googleMapsApiKey={apiKey || ''}
            libraries={googleMapsLibraries}
            loadingElement={
              <img
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                src={`https://maps.googleapis.com/maps/api/staticmap?key=${
                  apiKey || ''
                }&zoom=14&size=640x190&scale=2&markers=color:red%7C${
                  center.lat
                },${center.lng}&style=feature:poi%7Cvisibility:off`}
                alt="Placeholder Map"
              />
            }
          >
            <GoogleMapAPI
              mapContainerStyle={containerStyle}
              center={center}
              zoom={6}
              onLoad={onLoad}
              onUnmount={onUnmount}
              {...mapOptions}
            >
              {markerComponents}
            </GoogleMapAPI>
          </LoadScript>
        ) : (
          <GoogleMapAPI
            mapContainerStyle={containerStyle}
            center={center}
            zoom={6}
            onLoad={onLoad}
            onUnmount={onUnmount}
            options={{
              mapId,
            }}
          >
            {markerComponents}
          </GoogleMapAPI>
        )}
      </MapContainer>
    )
  },
)

export default StoreMap
